import { watch } from 'vue';
import { useStore } from 'vuex';
import MixpanelPluginV2 from '@satellite/plugins/MixpanelPluginV2.js';
import { useAnalytics, type AnalyticsClient } from '@satellite/composables/useAnalytics';
import { useSelectedWarehouse } from '@/composables/useSelectedWarehouse';
import config from '@/config/config';
import { isEmpty, isString } from 'lodash';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenterV2';

interface HeliosAnalyticsUser {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  orgId?: string | null;
  orgName?: string | null;
}

interface HeliosAnalyticsWarehouse {
  id?: string | number | null;
  name?: string | null;
}

export function createHeliosAnalyticsClient(): AnalyticsClient {
  const mixpanel = new MixpanelPluginV2({
    token: config.mixpanel_token ?? null,
    enabled: Boolean(config.mixpanel_token),
    debug: config.server_env === 'development'
  });
  mixpanel.init();

  return {
    track: (event, payload) => mixpanel.track(String(event), payload ?? undefined),
    identify: value => {
      if (!isString(value) || isEmpty(value)) {
        return;
      }
      mixpanel.identify(value);
    },
    reset: () => mixpanel.reset(),
    registerSuper: props => mixpanel.setProperties(props),
    setUserProperties: props => mixpanel.setUserProperties(props)
  };
}

export function useHeliosAnalytics() {
  const analytics = useAnalytics();
  const store = useStore();
  const { selectedWarehouse } = useSelectedWarehouse();

  const registerOrgSuperProperty = (me: HeliosAnalyticsUser) => {
    analytics.registerSuper({
      org_id: me.orgId ?? '',
      org_name: me.orgName ?? ''
    });
  };

  const registerUserSuperProperty = (me: HeliosAnalyticsUser) => {
    analytics.setUserProperties({
      $name: `${me.firstName ?? ''} ${me.lastName ?? ''}`,
      $email: me.email ?? '',
      'Nova User ID': me.id ?? '',
      'User Role': me.role ?? '',
      'Org ID': me.orgId ?? ''
    });
  };

  const registerWarehouseSuperProperty = (warehouse?: HeliosAnalyticsWarehouse | null) => {
    analytics.registerSuper({
      warehouse_id: String(warehouse?.id ?? ''),
      warehouse_name: String(warehouse?.name ?? '')
    });
  };

  watch(
    () => store.state.Auth.me,
    me => {
      if (me) {
        /**
         * distinct_id stays the user's email (not user.id) to preserve continuity with the
         * legacy MixpanelPlugin's Mixpanel cohorts/history for existing Helios users.
         */
        analytics.identify(me.email);
        registerOrgSuperProperty(me);
        registerUserSuperProperty(me);
      } else {
        analytics.reset();
      }
    },
    { immediate: true }
  );

  /**
   * `selectedWarehouse` type-checks as a nested Ref due to a pre-existing storeToRefs/useLocalStorageRef
   * typing quirk in useWarehouseStore; the runtime value is always a plain WarehouseDetails | null.
   */
  watch(
    () => selectedWarehouse.value as unknown as WarehouseDetails | null,
    (warehouse: WarehouseDetails | null) => registerWarehouseSuperProperty(warehouse),
    { immediate: true }
  );
}
